import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useState } from "react";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);

  const closeSheet = () => setIsOpen(false);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <div className="flex-shrink-0">
            <a href="/" className="text-xl sm:text-2xl font-bold text-foreground hover:text-primary transition-colors">
              tellcasey
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            <a href="/#product" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              Product
            </a>
            <a href="/#how-it-works" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              How it works
            </a>
            <a href="/#use-cases" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              Use cases
            </a>
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="text-sm font-medium bg-transparent hover:bg-transparent data-[state=open]:bg-transparent">
                    Resources
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="w-[300px] p-4 space-y-1">
                      <li>
                        <NavigationMenuLink asChild>
                          <Link
                            to="/guides"
                            className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                          >
                            <div className="text-sm font-medium leading-none">Guides</div>
                            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                              Explore all guides on customer proof automation
                            </p>
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link
                            to="/glossary"
                            className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                          >
                            <div className="text-sm font-medium leading-none">Glossary</div>
                            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                              Key terms for customer proof and GTM
                            </p>
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link
                            to="/templates"
                            className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                          >
                            <div className="text-sm font-medium leading-none">Templates</div>
                            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                              Case study, testimonial, and SOP templates
                            </p>
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link
                            to="/playbooks"
                            className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                          >
                            <div className="text-sm font-medium leading-none">Playbooks</div>
                            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                              Tactical GTM execution workflows
                            </p>
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link
                            to="/faq"
                            className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                          >
                            <div className="text-sm font-medium leading-none">FAQ</div>
                            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                              Common questions about customer proof
                            </p>
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link
                            to="/llm-snippets"
                            className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                          >
                            <div className="text-sm font-medium leading-none">LLM Snippets</div>
                            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                              AI-optimized snippets for customer proof
                            </p>
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link
                            to="/what-is-customer-proof-automation"
                            className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                          >
                            <div className="text-sm font-medium leading-none">What Is Customer Proof Automation?</div>
                            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                              The category definition
                            </p>
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link
                            to="/resources"
                            className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                          >
                            <div className="text-sm font-medium leading-none">Resource Library</div>
                            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                              Complete resource hub
                            </p>
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
            <a href="/#pricing" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              Pricing
            </a>
            <a href="/#faq" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              FAQ
            </a>
            <a href="/#contact" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              Contact
            </a>
          </nav>

          {/* CTAs and Mobile Menu */}
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="hidden sm:inline-flex" asChild>
              <a href="https://app.tellcasey.com/auth/login" target="_blank" rel="noopener noreferrer">Login</a>
            </Button>
            <Button variant="outline" size="sm" className="hidden sm:inline-flex" asChild>
              <Link to="/early-access">Book a Demo</Link>
            </Button>
            <Button size="sm" className="hidden sm:inline-flex" asChild>
              <Link to="/early-access">Get Started</Link>
            </Button>

            {/* Mobile Menu */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" aria-label="Open menu">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[350px]">
                <SheetHeader>
                  <SheetTitle className="text-left text-xl font-bold">tellcasey</SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col gap-1 mt-8">
                  <a 
                    href="/#product" 
                    onClick={closeSheet}
                    className="px-4 py-3 text-base font-medium text-foreground hover:bg-accent rounded-md transition-colors"
                  >
                    Product
                  </a>
                  <a 
                    href="/#how-it-works" 
                    onClick={closeSheet}
                    className="px-4 py-3 text-base font-medium text-foreground hover:bg-accent rounded-md transition-colors"
                  >
                    How it works
                  </a>
                  <a 
                    href="/#use-cases" 
                    onClick={closeSheet}
                    className="px-4 py-3 text-base font-medium text-foreground hover:bg-accent rounded-md transition-colors"
                  >
                    Use cases
                  </a>
                  
                  {/* Resources Collapsible */}
                  <Collapsible open={resourcesOpen} onOpenChange={setResourcesOpen}>
                    <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-3 text-base font-medium text-foreground hover:bg-accent rounded-md transition-colors">
                      Resources
                      <ChevronDown className={`h-4 w-4 transition-transform ${resourcesOpen ? 'rotate-180' : ''}`} />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pl-4">
                      <Link 
                        to="/guides" 
                        onClick={closeSheet}
                        className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                      >
                        Guides
                      </Link>
                      <Link 
                        to="/glossary" 
                        onClick={closeSheet}
                        className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                      >
                        Glossary
                      </Link>
                      <Link 
                        to="/templates" 
                        onClick={closeSheet}
                        className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                      >
                        Templates
                      </Link>
                      <Link 
                        to="/playbooks" 
                        onClick={closeSheet}
                        className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                      >
                        Playbooks
                      </Link>
                      <Link 
                        to="/faq" 
                        onClick={closeSheet}
                        className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                      >
                        FAQ
                      </Link>
                      <Link 
                        to="/llm-snippets" 
                        onClick={closeSheet}
                        className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                      >
                        LLM Snippets
                      </Link>
                      <Link 
                        to="/what-is-customer-proof-automation" 
                        onClick={closeSheet}
                        className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                      >
                        What Is Customer Proof Automation?
                      </Link>
                      <Link 
                        to="/resources" 
                        onClick={closeSheet}
                        className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                      >
                        Resource Library
                      </Link>
                    </CollapsibleContent>
                  </Collapsible>

                  <a 
                    href="/#pricing" 
                    onClick={closeSheet}
                    className="px-4 py-3 text-base font-medium text-foreground hover:bg-accent rounded-md transition-colors"
                  >
                    Pricing
                  </a>
                  <a 
                    href="/#faq" 
                    onClick={closeSheet}
                    className="px-4 py-3 text-base font-medium text-foreground hover:bg-accent rounded-md transition-colors"
                  >
                    FAQ
                  </a>
                  <a 
                    href="/#contact" 
                    onClick={closeSheet}
                    className="px-4 py-3 text-base font-medium text-foreground hover:bg-accent rounded-md transition-colors"
                  >
                    Contact
                  </a>

                  {/* Mobile CTAs */}
                  <div className="flex flex-col gap-3 mt-6 px-4">
                    <Button variant="ghost" asChild onClick={closeSheet}>
                      <a href="https://app.tellcasey.com/auth/login" target="_blank" rel="noopener noreferrer">Login</a>
                    </Button>
                    <Button variant="outline" asChild onClick={closeSheet}>
                      <Link to="/early-access">Book a Demo</Link>
                    </Button>
                    <Button asChild onClick={closeSheet}>
                      <Link to="/early-access">Get Started</Link>
                    </Button>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};
